import {
  type CanvasElementFrom,
  type IoConfig,
  type PartialPropsOf,
  type PropsOf,
  type StyleConfigProps,
  type TransformsElementProps,
  type VisibilityElementProps,
  type WithIoConfig,
  type WithProps,
} from '@blaze-runtime/engine-domain';
import { type FontWeightOption } from '@blaze-shared/api-contracts-domain';
import {
  type BorderRadiusValue,
  type CanvasElementPropertiesWithData,
  type ContainerActionConfig,
  type ElementHorizontalAlignment,
  type ElementSizingMode,
  type FontOptions,
  type LabelIconAlignment,
  type PropertiesConfig,
  type PropertiesConfigInputColorModel,
  type PropertiesConfigInputSpacingAndAlignmentModel,
  type PropertiesConfigInputVisualEffectModel,
} from '@blaze-shared/elements-domain';
import { type ValueWithUnitOfMeasure } from '@blaze-shared/shared-util';
import { DEFAULT_ICON_TO_TEXT_RATIO } from '@blaze-shared/theming-domain';

import { registerElementDefinition } from '../../services/element-definitions.service';
import {
  propsConfigHelper as epc,
  relabelSizingMinToFitContent,
} from '../../utils/elements-props-config.helpers';

import { ButtonDefinitionMapper } from './ai/button.definition-mapper';
import { ButtonElementHandler } from './ai/button.element-handler';
import { ButtonOutputEmitGuard } from './button-output-emit.guard';
import { ButtonValidationHandler } from './button-validation-handler.service';
import { ButtonComponent } from './button.component';
import { ButtonPropsBuilder } from './button.props-builder';
import { buttonV2AddActionPropsMigration } from './migrations/002-button-add-action-props.migration';
import { buttonV3PaddingToMarginMigration } from './migrations/003-button-padding-to-margin.migration';

export { transformToButton } from './button.transforms';
export type ButtonDef = WithProps<ButtonPropsModel> & WithIoConfig;
export type ButtonElement = CanvasElementFrom<'button'>;

// Extend the CanvasElementDefinitions type
declare module '@blaze-runtime/engine-domain' {
  interface CanvasElementDefinitions {
    button: ButtonDef;
  }
}

const DEFAULT_WIDTH = 168;
const MIN_SIZE = 48;
const MAX_WIDTH = 900;
const MAX_HEIGHT = 150;
const WIDTH_OPTIONS: ElementSizingMode[] = ['fixed', 'full', 'min'];
const HEIGHT_OPTIONS: ElementSizingMode[] = ['fixed', 'min'];

registerElementDefinition({
  type: 'button',
  componentType: ButtonComponent,
  outputEmitGuard: ButtonOutputEmitGuard,
  validationHandler: ButtonValidationHandler,
  isListed: true,
  name: 'Button',
  width: { mode: 'value', value: DEFAULT_WIDTH, fixed: true },
  height: { mode: 'value', value: MIN_SIZE },
  ioConfig: getIoConfig(),
  propsConfig: getPropsConfig(),
  defaults: getDefaults(),
  initial: getInitial(),
  propsBuilder: ButtonPropsBuilder,
  toolbar: ['triggerClick'],
  styleIncludePaths: ['style'],
  metaUiFeatures: {
    type: 'button',
  },
  features: {
    loadingIndication: 'fadeWithoutIndicator',
    preventAutoHeight: true,
    ai: {
      description: true,
    },
  },
  ai: {
    name: 'button',
    handler: () => ButtonElementHandler,
    definitionMapper: () => ButtonDefinitionMapper,
  },
  version: 3,
  migrations: {
    2: buttonV2AddActionPropsMigration,
    3: buttonV3PaddingToMarginMigration,
  },
});

function getIoConfig(): IoConfig {
  return {
    inputs: [
      {
        name: 'visible',
        bindingDefs: [{ property: 'visible', label: 'Is Visible', dataType: 'Boolean' }],
      },
    ],
    outputs: [{ eventType: 'click', schema: null }],
  };
}

type PropsModel = CanvasElementPropertiesWithData &
  StyleConfigProps &
  TransformsElementProps &
  VisibilityElementProps;

export type ButtonPropsModel = PropsModel & {
  content: {
    button: {
      label: string;
      icon?: string;
      iconPosition?: LabelIconAlignment;
      showLoader?: boolean;
    };
    action?: ContainerActionConfig;
  };
  style: {
    label: {
      color: PropertiesConfigInputColorModel;
      align: ElementHorizontalAlignment;
      iconSize: ValueWithUnitOfMeasure;
    };
    font: FontOptions;
    button: {
      offset: PropertiesConfigInputSpacingAndAlignmentModel;
      fill: PropertiesConfigInputColorModel;
      outline: PropertiesConfigInputColorModel;
      outlineWidth: ValueWithUnitOfMeasure;
      radius: BorderRadiusValue;
      visualEffect?: PropertiesConfigInputVisualEffectModel;
    };
  };
};

function getDefaults(): PropsOf<'button'> {
  return {
    content: {
      button: {
        label: 'Button',
      },
    },
    style: {
      label: {
        color: { color1: '$t.accentLabel', selectorType: 'solid' },
        align: 'center',
        iconSize: '24px',
      },
      font: {
        style: [],
        fontFamily: '$t.fontFamily',
        fontWeight: '$t.labelFontWeight' as FontWeightOption,
        lineHeight: '$t.lineHeight',
        fontSize: '$t.fontSize',
      },
      button: {
        offset: { margin: '$t.spacingInput', padding: '8px' },
        fill: { color1: '$t.accent', selectorType: 'solid' },
        outline: { color1: '$t.outline', selectorType: 'none' },
        outlineWidth: '1px',
        radius: '$t.radius',
      },
    },
  };
}

function getInitial(): PartialPropsOf<'button'> {
  return {
    style: {
      label: {
        iconSize: '$t.buttonIconSize',
      },
    },
  };
}

function getPropsConfig(): PropertiesConfig {
  return epc.initConfig('button', [
    epc.section('content', 'Content', [
      epc.subsections.subsection('content.button', 'Button', [
        epc.fields.textInput('content.button.label', 'Label'),
        epc.fields.iconField('content.button.icon'),
        epc.fields.iconPositionField('content.button.iconPosition', 'content.button.icon'),
        epc.fields.toggleField('content.button.showLoader', 'Show loaders for long actions'),
      ]),

      epc.subsections.subsection(
        'content',
        'Action',
        [epc.fields.buttonActionField('content.action')],
        { hideLabel: true },
      ),

      epc.subsections.visibilitySubsection(),

      epc.subsections.subsection('content.button', 'Size', [
        epc.root.elementWidthField(
          { min: MIN_SIZE, max: MAX_WIDTH, unit: 'px', step: 1 },
          WIDTH_OPTIONS,
          relabelSizingMinToFitContent,
        ),
        epc.root.elementHeightField(
          { min: MIN_SIZE, max: MAX_HEIGHT, unit: 'px', step: 1 },
          HEIGHT_OPTIONS,
          relabelSizingMinToFitContent,
        ),
      ]),
    ]),
    epc.section(
      'style',
      'Style',
      [
        epc.subsections.styleSheetSubsection(),

        epc.subsections.subsection('style.button', 'Padding & Margin', [
          epc.fields.spacingAndAlignField('style.button.offset', {
            hasMargin: true,
            hasPadding: true,
          }),
        ]),

        epc.subsections.subsection('style.label', 'Label', [
          epc.fields.colorField('style.label.color', 'Label', {
            allowGradient: false,
            allowNone: false,
          }),
          ...epc.fields.fontFamilyAndWeightFields('style.font.fontFamily', 'style.font.fontWeight'),
          epc.fields.horizontalAlignField('style.label.align'),
          epc.fields.fontStyleField('style.font.style', 'Style', ['line-through', 'bold']),
          epc.utils.observedField(
            epc.fields.sliderField('style.font.fontSize', 'Font size', [
              { min: 8, max: 72, unit: 'px' },
            ]),
            (model: ButtonPropsModel, value: ValueWithUnitOfMeasure) => {
              const fontSizeNumber = Number.parseInt(value);
              if (Number.isNaN(fontSizeNumber)) {
                // If number is unknown, return default icon size
                return { 'style.label.iconSize': '$t.buttonIconSize' };
              }

              // Calculate icon size based on font size
              const iconSizeString = `${Math.round(fontSizeNumber * DEFAULT_ICON_TO_TEXT_RATIO)}px`;
              return { 'style.label.iconSize': iconSizeString };
            },
          ),

          epc.fields.sliderField('style.label.iconSize', 'Icon size', [
            { min: 8, max: 48, unit: 'px' },
          ]),
        ]),

        epc.subsections.subsection('style.button', 'Button', [
          epc.fields.colorField('style.button.fill', 'Fill', { allowNone: false }),
          epc.utils.observedField(
            epc.fields.colorField('style.button.outline', 'Outline', { allowGradient: false }),
            (model: ButtonPropsModel, value: PropertiesConfigInputColorModel) => {
              if (
                value.selectorType !== 'none' &&
                model.style.button.outline.selectorType === 'none' &&
                model.style.button.outlineWidth === '0px'
              ) {
                return { 'style.button.outlineWidth': '1px' };
              }
              return {};
            },
          ),
          epc.utils.conditionalField(
            epc.fields.sliderField('style.button.outlineWidth', 'Width', [
              { min: 0, max: 10, unit: 'px' },
            ]),
            'style.button.outline',
            (value: PropertiesConfigInputColorModel) => value.selectorType === 'none',
          ),
          epc.fields.radiusField('style.button.radius'),
          ...epc.fields.visualEffectField('style.button.visualEffect'),
        ]),
      ],
      true,
    ),
  ]);
}
